import * as React from 'react';
import { di } from 'react-magnetic-di';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { open } from '@atlassian/help-center-common-util/location';
import type { Props as GroupBoxProps } from '../group-box';
import GroupBox from '../group-box';
import { StyledLink } from '../styled';

export interface BaseGroupBoxLinkProps extends GroupBoxProps {
	selected?: boolean;
	onClick?: () => void;
	icon?: React.ReactNode;
	target?: '_blank' | '_self' | '_parent' | '_top';
	actionSubjectId: string;
	dataTestId?: string;
}

interface InternalGroupBoxLinkProps extends BaseGroupBoxLinkProps {
	to: string;
	editRequestTypeOnRequestList?: React.ReactElement;
}

interface ExternalGroupBoxLinkProps extends BaseGroupBoxLinkProps {
	href: string;
}

type GroupBoxLinkProps = InternalGroupBoxLinkProps | ExternalGroupBoxLinkProps;

const isExternalLink = (target: EventTarget | null): boolean => {
	return target instanceof HTMLElement && target.className.includes('external-link');
};

const handleExternalLink = (e: React.MouseEvent | React.KeyboardEvent): void => {
	e.preventDefault();
	open(e.target.toString(), '_blank');
};

const GroupBoxLink: React.FunctionComponent<GroupBoxLinkProps & WithAnalyticsEventsProps> = ({
	className,
	selected,
	onClick,
	target,
	dataTestId,
	actionSubjectId,
	...rest
}) => {
	di(useIsPageInEditMode);
	const [isPageInEditMode] = useIsPageInEditMode();
	const tabIndex = isPageInEditMode ? -1 : undefined;

	const handleClick = React.useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			if (isPageInEditMode) return;
			if (isExternalLink(e.target)) {
				handleExternalLink(e);
				return;
			}
			onClick?.();
		},
		[isPageInEditMode, onClick],
	);

	if ('to' in rest) {
		// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
		// oxlint-disable-next-line no-shadow
		const { to, editRequestTypeOnRequestList, ...forGroupBox } = rest;
		const showEditRequestTypeButton = !isPageInEditMode && !!editRequestTypeOnRequestList;
		const contentWithTo = showEditRequestTypeButton ? (
			<StyledLink
				selected={!!selected}
				to={to}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
				className={className}
				onClick={handleClick}
				target={target}
				actionSubjectId={actionSubjectId}
				data-test-id={dataTestId}
			>
				<GroupBox {...forGroupBox} editRequestTypeOnRequestList={editRequestTypeOnRequestList} />
			</StyledLink>
		) : (
			<StyledLink
				selected={!!selected}
				to={to}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
				className={className}
				onClick={handleClick}
				target={target}
				actionSubjectId={actionSubjectId}
				data-test-id={dataTestId}
				tabIndex={tabIndex}
			>
				<GroupBox {...forGroupBox} />
			</StyledLink>
		);
		return isPageInEditMode ? <Box xcss={disabledLink}>{contentWithTo}</Box> : contentWithTo;
	}
	const { href, ...forGroupBox } = rest;
	const content = (
		<StyledLink
			selected={!!selected}
			href={href}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
			className={className}
			onClick={handleClick}
			target={target}
			actionSubjectId={actionSubjectId}
			data-test-id={dataTestId}
			tabIndex={tabIndex}
		>
			<GroupBox {...forGroupBox} />
		</StyledLink>
	);
	return isPageInEditMode ? <Box xcss={disabledLink}>{content}</Box> : content;
};

const disabledLink = xcss({
	pointerEvents: 'none',
});

export default GroupBoxLink;
